import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  Trash2, 
  Upload, 
  X,
  Edit2,
  Package,
  Tags
} from "lucide-react";

import { toast } from "sonner";

interface AdminProductsProps {
  products: any[];
  categories: string[];
  addProduct: (p: any) => Promise<void>;
  updateProduct: (id: string, p: any) => Promise<void>;
  removeProduct: (id: string) => Promise<void>;
  addCategory: (c: string) => void;
  removeCategory: (c: string) => void;
}

export function AdminProducts({ 
  products, 
  categories, 
  addProduct, 
  updateProduct, 
  removeProduct,
  addCategory,
  removeCategory
}: AdminProductsProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState("");
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    price: "",
    category: categories[1] || categories[0] || "",
    images: [] as string[]
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const img = new Image();
          img.src = reader.result as string;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 800;
            const MAX_HEIGHT = 800;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);
            
            const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
            setNewProduct(prev => ({ 
              ...prev, 
              images: [...prev.images, compressedDataUrl] 
            }));
          };
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index: number) => {
    setNewProduct(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSaveProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      toast.error("Nome e preço são obrigatórios.");
      return;
    }
    
    const productData = {
      name: newProduct.name,
      description: newProduct.description,
      price: parseFloat(newProduct.price),
      category: newProduct.category,
      images: newProduct.images
    };

    try {
      if (editingId) {
        await updateProduct(editingId, productData);
        setEditingId(null);
      } else {
        await addProduct(productData);
      }
      
      setNewProduct({
        name: "",
        description: "",
        price: "",
        category: categories[1] || categories[0] || "",
        images: []
      });
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar produto");
    }
  };

  const startEdit = (product: any) => {
    setEditingId(product.id);
    setNewProduct({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      images: product.images
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setNewProduct({
      name: "",
      description: "",
      price: "",
      category: categories[1] || categories[0] || "",
      images: []
    });
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-8">
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">
              {editingId ? "Editar Produto" : "Adicionar Novo Produto"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome do Produto</Label>
                <Input 
                  placeholder="Ex: Bastidor Floral" 
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Preço (R$)</Label>
                <Input 
                  type="number" 
                  placeholder="0.00" 
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <select 
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  value={newProduct.category}
                  onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                >
                  <option value="" disabled>Selecione uma categoria</option>
                  {categories.filter(c => c !== "Todos").map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Imagens do Produto (Múltiplas)</Label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-2">
                  {newProduct.images.map((img, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden border">
                      <img src={img} alt={`Preview ${index}`} className="w-full h-full object-cover" />
                      <button 
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-pink-300 hover:bg-pink-50/50 transition-all bg-gray-50/50"
                  >
                    <Upload className="h-5 w-5 text-gray-400" />
                    <span className="text-[10px] font-medium text-gray-500">Adicionar</span>
                  </div>
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  className="hidden" 
                  accept="image/*" 
                  multiple
                  onChange={handleImageUpload}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Descrição</Label>
                <Textarea 
                  placeholder="Fale mais sobre o produto..." 
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  className="min-h-[100px]"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveProduct} className="flex-1 bg-pink-500 hover:bg-pink-600">
                {editingId ? "Atualizar Produto" : "Salvar Produto"}
              </Button>
              {editingId && (
                <Button variant="outline" onClick={cancelEdit}>
                  Cancelar Edição
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Catálogo de Produtos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {products.length === 0 ? (
                <p className="text-gray-500 text-center py-8 col-span-full">Nenhum produto cadastrado.</p>
              ) : (
                products.map(product => (
                  <div key={product.id} className="flex items-center gap-4 p-4 border rounded-2xl group hover:border-pink-200 transition-colors">
                    <div className="h-16 w-16 rounded-xl overflow-hidden border bg-gray-50 shrink-0">
                      {product.images[0] ? (
                        <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-6 w-6 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 truncate">{product.name}</h4>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-pink-500">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(product.price)}
                        </span>
                        <Badge variant="outline" className="text-[9px] h-4">{product.category}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500" onClick={() => startEdit(product)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeProduct(product.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
